import {
  Ban,
  CheckCircle2,
  CreditCard,
  FilePen,
  Mail,
  Receipt,
  ShieldAlert,
  Undo2,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { InvoiceEventKind } from "@api/shared-runtime/enums";
import { ActivityTimeline } from "@/components/activity-timeline";
import { formatUsageBillingCurrency } from "./usage-billing-format";
import type { UnitInvoice } from "./usage-billing-invoice-presentation";

type InvoiceEvent = UnitInvoice["events"][number];

const DAILY_JOB_ACTOR = "the daily job";

function byActor(event: InvoiceEvent): string {
  return event.actorName ? ` by ${event.actorName}` : "";
}

function attempt(event: InvoiceEvent): string {
  return event.attempt ? ` (attempt ${event.attempt})` : "";
}

function describeEvent(event: InvoiceEvent): {
  label: string;
  Icon: LucideIcon;
  accent?: string;
} {
  switch (event.kind) {
    case InvoiceEventKind.Generated:
      return {
        label: `Generated by ${event.actorName ?? DAILY_JOB_ACTOR}`,
        Icon: FilePen,
      };
    case InvoiceEventKind.Issued:
      return { label: `Issued${byActor(event)}`, Icon: FilePen };
    case InvoiceEventKind.Emailed:
      return {
        label: `Invoice emailed to ${event.message ?? "the billing contact"}${byActor(event)}`,
        Icon: Mail,
      };
    case InvoiceEventKind.Resent:
      return {
        label: `Invoice re-sent to ${event.message ?? "the billing contact"}${byActor(event)}`,
        Icon: Mail,
      };
    case InvoiceEventKind.ChargeSubmitted:
      return {
        label: `Charge submitted${attempt(event)}${byActor(event)}`,
        Icon: CreditCard,
      };
    case InvoiceEventKind.ChargeDeclined:
      return {
        label: `Declined${attempt(event)}: ${event.message ?? "payment failed"}`,
        Icon: XCircle,
        accent: "text-rose-600",
      };
    case InvoiceEventKind.AuthenticationRequired:
      return {
        label: `Card needs customer authentication${attempt(event)}`,
        Icon: ShieldAlert,
        accent: "text-amber-700",
      };
    case InvoiceEventKind.Paid:
      return {
        label: `Paid ${formatUsageBillingCurrency(event.amount ?? "0")}${event.message ? ` with ${event.message}` : ""}`,
        Icon: CheckCircle2,
        accent: "text-emerald-600",
      };
    case InvoiceEventKind.Refunded:
      return {
        label: `Refunded ${formatUsageBillingCurrency(event.amount ?? "0")}`,
        Icon: Undo2,
      };
    case InvoiceEventKind.Voided:
      return {
        label: `Voided${byActor(event)}`,
        Icon: Ban,
        accent: "text-slate-500",
      };
    case InvoiceEventKind.ReceiptSent:
      return {
        label: `Receipt sent to ${event.message ?? "the billing contact"}`,
        Icon: Receipt,
      };
  }
}

export function UsageBillingInvoiceActivity({
  events,
}: {
  events: InvoiceEvent[];
}) {
  return (
    <ActivityTimeline
      defaultOpen
      entries={events.map((event) => ({
        key: event.id,
        at: event.occurredAt,
        ...describeEvent(event),
      }))}
    />
  );
}
